import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import jsCookie from 'js-cookie';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { IAuth } from '@/modules/auth/types';
import axiosClient from '@/shared/lib/axios';

/***************************** logout *****************************/
export const useLogout = () => {
  return useMutation(
    async () => await axiosClient.post('/company/portalInsights/logout')
  );
};

/***************************** identifier checker *****************************/
export const useCheckIdentifier = () => {
  return useMutation({
    mutationKey: ['checkIdentifier'],
    mutationFn: (
      identifier: string
    ): Promise<{ data: IAuth.ICheckIdentifierResponse }> =>
      axiosClient.post(`/company/portalInsights/check-identifier`, {
        identifier,
      }),
  });
};

/***************************** otp *****************************/
export const useSendOTP = (data: IAuth.ILoginPhoneNumberSchema) => {
  return useQuery({
    queryKey: ['sendOTP', data],
    queryFn: () => axiosClient.post('/company/sendEyeOtp', data),
    enabled: !!data.country && !!data.phoneNumber,
  });
};
export const useValidateOTP = () => {
  return useMutation({
    mutationKey: ['validateOTP'],
    mutationFn: (
      validateOTPData: IAuth.IValidateOTPSchema
    ): Promise<{ data: IAuth.IValidOTPResponse }> =>
      axiosClient.post(
        `/company/portalInsights/validate-eye-otp`,
        validateOTPData
      ),
  });
};

/***************************** login *****************************/
const loginAuthntication = async (
  authData: IAuth.ILoginSchema
): Promise<IAuth.ILoginRequestResponse> => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/login`,
    authData
  );
  return data;
};
export const useLoginAuthntication = () => {
  return useMutation({
    mutationKey: ['loginAuthntication'],
    mutationFn: loginAuthntication,
  });
};

/***************************** forgot password *****************************/
export const useForgotPassword = () => {
  const { t } = useTranslation('login');
  return useMutation({
    mutationKey: ['forgotPassword'],
    mutationFn: (
      userData: IAuth.ILoginPhoneNumberSchema | IAuth.ILoginEmailSchema
    ) => axiosClient.post(`/company/portalInsights/forget-password`, userData),
    onSuccess: () => {
      toast.success(t('reset-link-sent'));
    },
  });
};

/***************************** reset/create password *****************************/
export const useResetPassword = () => {
  return useMutation({
    mutationKey: ['resetPassword'],
    mutationFn: (newPassword: string) =>
      axiosClient.post(`/company/portalInsights/reset-password`, {
        newPassword,
      }),
  });
};

/***************************** signup *****************************/
const signupWithCreds = async (
  creds: IAuth.ISignupSchema
): Promise<IAuth.ISignupRequestResponse> => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/signup/request`,
    creds
  );
  return data;
};
export const useSignupwWithCreds = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: signupWithCreds,
    onSuccess: () => queryClient.invalidateQueries(['checkCompanyUser']),
  });
};

/***************************** company logo *****************************/
export const getCompanyLogo = async (
  companyId: number | null
): Promise<{ logo: string }> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/settings/company/${companyId}`
  );
  return data;
};
export const useGetCompanyLogo = (companyId: number | null) => {
  return useQuery(
    ['getCompanyDetails', companyId],
    () => getCompanyLogo(companyId),
    {
      enabled: Boolean(companyId),
      staleTime: Infinity,
      // enabled: false,
    }
  );
};

/***************************** check company user *****************************/
const checkCompanyUser = async (): Promise<IAuth.ICheckCompanyUserResponse> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/company-user`
  );
  return data;
};

export const useCheckCompanyUser = () => {
  const token = jsCookie.get('token');
  const userObj = jsCookie.get('userObj');
  const user = JSON.parse(userObj ?? '{}')?.value;
  return useQuery({
    queryKey: ['checkCompanyUser'],
    queryFn: checkCompanyUser,
    // call only once
    staleTime: Infinity,
    enabled: Boolean(token && user),
  });
};
/************************************ GET USER INFO *********************************************/
export const getUserInfo = async (): Promise<IAuth.IUser> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/user-profile`
  );
  return data;
};

export const useGetUserInfo = (isCompanyUser?: boolean) => {
  const token = jsCookie.get('token');
  return useQuery({
    queryKey: ['getUserInfo'],
    queryFn: getUserInfo,
    enabled: Boolean(token) && isCompanyUser,
  });
};
/***************************** job titles *****************************/
const getCompanyJobTitles =
  async (): Promise<IAuth.IGetCompanyJobTitlesResponse> => {
    const { data } = await axiosClient.get(
      `/company/portalInsights/company-job-titles`
    );
    return data;
  };

export const useGetCompanyJobTitles = () => {
  return useQuery({
    queryKey: ['getCompanyJobTitles'],
    queryFn: getCompanyJobTitles,
  });
};

//complete signup
const completeSignUp = async (
  obj: IAuth.ICompleteSignupForm
): Promise<IAuth.IAuthSuccessResponse> => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/signup`,
    obj
  );
  return data;
};

export const useCompleteSignup = () => {
  return useMutation({
    mutationFn: completeSignUp,
  });
};
